import S, { DataSignal } from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import { IAjax } from 'lib/Ajax';
import { AssertExtendedBy } from 'lib/assertExtends';
import { DraftArray, MergeByObjectIdentity } from 'lib/draftArray';
import { DraftObject } from 'lib/draftSignals';
import { Mutex } from 'lib/mutex';
import objectCreate from 'lib/objectCreate';
import TZDate from 'lib/TZDate';
import { ValidationWatcher, Errors } from 'lib/validationWatcher';

import { BerthReservation, BerthReservationOptions } from 'data/models/BerthReservation';
import { CargoActivity } from 'data/models/CargoActivity';
import { CargoActivityType } from 'data/models/CargoActivityType';
import { CargoCategory } from 'data/models/CargoCategory';
import { CruiseActivity } from 'data/models/CruiseActivity';
import { Measurement } from 'data/models/Measurement';
import { TradeRoute } from 'data/models/TradeRoute';
import { VesselSide } from 'server/enums/BerthReservation';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

import {
    FootmarkAssignments,
    FootmarkAssignmentsParams,
    FootmarkAssignmentsReservation
} from 'portcall/footmarkassignments/model';

export interface InPortOpsParams {
    Reservation : InPortOpsReservation;
    CanEdit : () => boolean;
    AddNewCargoActivity  : () => void;
    AddNewCruiseActivity : () => void;
    RemoveCargoActivity  : (act : CargoActivity) => void;
    RemoveCruiseActivity : (act : CruiseActivity) => void;
    CargoActivityHasUnsavedChanges  : (act : CargoActivity) => boolean;
    CruiseActivityHasUnsavedChanges : (act : CruiseActivity) => boolean;

    /**
     * A signal that evaluates to true provided at least one cargo or cruise activity has been persisted for
     * this reservation or it has a persisted `HasLayOpsOnly` value. I.e. this signal returns true provided
     * some port ops info has been persisted for this reservation.
     */
    HasAnyPortOpsPersisted : () => boolean;

    /**
     * An optional save handler that knows how to persist this reservation's in-port activities. If provided,
     * the In Port Ops UI will provide save controls for persisting in-port activities. Otherwise, calling
     * code must construct appropriate save controls itself outside of the In Port Ops UI.
     */
    ActivitiesSaveHandler? : SaveHandler;
    /**
     * An optional save handler that knows how to persist this reservation's Footmark and SideTo values. If
     * provided, the In Port Ops UI will provide save controls for persisting these values. Otherwise, calling
     * code must construct appropraite save controls istelf outside of the In Port Ops UI.
     */
    PierPositionSaveHandler? : SaveHandler;
}

interface _InPortOpsReservation extends FootmarkAssignmentsReservation {
    CancelledDate : () => TZDate | null;
    HasLayOpsOnly : DataSignal<boolean>;
    CargoActivities  : ArraySignal<CargoActivity >;
    CruiseActivities : ArraySignal<CruiseActivity>;
}
export type InPortOpsReservation = AssertExtendedBy<_InPortOpsReservation, BerthReservation>;

export interface SaveHandler {
    HasUnsavedChanges : () => boolean;
    IsSaving : () => boolean;
    PerformSave : () => void;
    QuitChanges : () => void;
    Validation : () => Errors;
}

export interface NewReservationInPortOpsParams {
    Reservation : BerthReservation;
}

export function NewReservationInPortOps(
    data : PCCache,
    inPortOpsModel : (params : InPortOpsParams) => InPortOps,
    { Reservation } : NewReservationInPortOpsParams
) {
    // Wire up navigation props to reference data for cargo activities.
    Reservation.CargoActivities.map(act => data.CargoActivities.attach(act, [
        act.ActivityType,
        act.CargoCategory,
        act.TradeRoute,
    ]));

    return inPortOpsModel({
        Reservation,
        CanEdit: () => true,
        AddNewCargoActivity,
        AddNewCruiseActivity,
        RemoveCargoActivity,
        RemoveCruiseActivity,
        // Any cargo or cruise activity must have unsaved changes b/c ALL of this data is unsaved.
        CargoActivityHasUnsavedChanges : () => true,
        CruiseActivityHasUnsavedChanges: () => true,
        // Can't have any port ops persisted b/c this is a brand new reservation.
        HasAnyPortOpsPersisted: () => false,
        // NO save handlers. For new reservations, persistence has to be handled outside of the In Port Ops UI,
        // presumably via whatever endpoint is being used to persist the new reservation as a whole.
    });

    function AddNewCargoActivity() {
        Reservation.CargoActivities.push(new CargoActivity());
    }

    function AddNewCruiseActivity() {
        Reservation.CruiseActivities.push(new CruiseActivity());
    }

    function RemoveCargoActivity(act : CargoActivity) {
        Reservation.CargoActivities.remove(act);
    }

    function RemoveCruiseActivity(act : CruiseActivity) {
        Reservation.CruiseActivities.remove(act);
    }
}


export interface ExistingReservationInPortOpsParams {
    Reservation : BerthReservation;
    CanEdit : () => boolean;
    SaveMutex : Mutex;
}

interface DraftActivityMetadata<T> {
    original : T;
    draftObject : DraftObject<T>;
}

export function ExistingReservationInPortOps(
    ajax : IAjax,
    entrypoints : Entrypoints,
    data : PCCache,
    inPortOpsModel : (params : InPortOpsParams) => InPortOps,
    {
        Reservation,
        CanEdit,
        SaveMutex
    } : ExistingReservationInPortOpsParams
) {
    const
        hasLayOpsOnlyDraft = DraftObject(Reservation, ['HasLayOpsOnly']),
        cargoOriginals = DraftArray(Reservation.CargoActivities, MergeByObjectIdentity),
        cargoDraftToMetadata = new WeakMap<CargoActivity, DraftActivityMetadata<CargoActivity>>(),
        // Make a draft object for each activity in our cargoOriginals DraftArray.
        cargoDrafts = cargoOriginals.signal.map(act => {
            const draft = DraftObject(act, [
                'ActivityTypeId',
                'CargoCategoryId',
                'TradeRouteId',
                'Quantity.Num',
                'Quantity.UOM'
            ]);
            draft.object.ActivityType  = S.value(S.sample(act.ActivityType ));
            draft.object.CargoCategory = S.value(S.sample(act.CargoCategory));
            draft.object.TradeRoute    = S.value(S.sample(act.TradeRoute   ));
            data.CargoActivities.attach(draft.object, [
                draft.object.ActivityType,
                draft.object.CargoCategory,
                draft.object.TradeRoute,
            ]);
            cargoDraftToMetadata.set(draft.object, { original: act, draftObject: draft });
            return draft;
        }),
        cruiseOriginals = DraftArray(Reservation.CruiseActivities, MergeByObjectIdentity),
        cruiseDraftToMetadata = new WeakMap<CruiseActivity, DraftActivityMetadata<CruiseActivity>>(),
        // Make a draft object for each activity in our cruiseOriginals DraftArray.
        cruiseDrafts = cruiseOriginals.signal.map(act => {
            const draft = DraftObject(act);
            cruiseDraftToMetadata.set(draft.object, { original: act, draftObject: draft });
            return draft;
        }),
        pierPositionDraft = DraftObject(Reservation, [
            'Footmark.Num',
            'Footmark.UOM',
            'Length.Num',
            'Length.UOM',
            'SideTo'
        ]),
        merged : InPortOpsReservation = objectCreate(Reservation);

    merged.HasLayOpsOnly = hasLayOpsOnlyDraft.object.HasLayOpsOnly;
    merged.CargoActivities = cargoDrafts.map(draft => draft.object);
    merged.CruiseActivities = cruiseDrafts.map(draft => draft.object);
    merged.Footmark = pierPositionDraft.object.Footmark;
    merged.Length = pierPositionDraft.object.Length;
    merged.SideTo = pierPositionDraft.object.SideTo;

    // We don't ordinarily do much if any diff'ing when tracking whether a draft object has unsaved changes or
    // not. But we'll make an exception and do som very minimal diff'ing in this case b/c it makes the labeling
    // around Lay Only reservations a little clearer.
    S(() =>
        hasLayOpsOnlyDraft.methods.isCaching()
     && Reservation.HasLayOpsOnly() === hasLayOpsOnlyDraft.object.HasLayOpsOnly()
     && hasLayOpsOnlyDraft.methods.checkpoint().revert()
    );
    S(() =>
        cargoOriginals.methods.isCaching()
     && Reservation.CargoActivities().length === 0
     && cargoOriginals.signal().length === 0
     && cargoOriginals.methods.checkpoint().revert()
    );
    S(() =>
        cruiseOriginals.methods.isCaching()
     && Reservation.CruiseActivities().length === 0
     && cruiseOriginals.signal().length === 0
     && cruiseOriginals.methods.checkpoint().revert()
    );

    const HasAnyPortOpsPersisted = () =>
        Reservation.HasLayOpsOnly()
     || Reservation.CargoActivities().length > 0
     || Reservation.CruiseActivities().length > 0;

    // Save handlers
    const
        isSavingActivities = S.value(false),
        activitiesClient = ajax.Url<BerthReservationOptions, BerthReservationOptions>(
            entrypoints.UpdateInPortActivities({ Id: Reservation.Id() }),
            // DON'T use our data cache, as we want finer control of the DataCacheCollection.update calls so
            // we can specify template objects. See SaveActivities implementation below.
            { useCache: false }
        ),
        activitiesValidation = ValidationWatcher(activitiesClient),
        // TODO: Some designs have shown separate save buttons for cargo vs cruise. But that adds implementation
        // complexity b/c the Lay Only flag depends on the combined state of cargo AND cruise activities. For
        // now just using a single save button to persist both activity types.
        ActivitiesSaveHandler : SaveHandler = {
            HasUnsavedChanges: S(() =>
                hasLayOpsOnlyDraft.methods.isCaching()
             || cargoOriginals .methods.isCaching()
             || cruiseOriginals.methods.isCaching()
             || cargoDrafts ().some(d => d.methods.isCaching())
             || cruiseDrafts().some(d => d.methods.isCaching())
            ),
            IsSaving: () => isSavingActivities(),
            PerformSave: SaveActivities,
            QuitChanges() {
                S.freeze(() => {
                    hasLayOpsOnlyDraft.methods.checkpoint().revert();
                    cargoOriginals .methods.checkpoint().revert();
                    cruiseOriginals.methods.checkpoint().revert();
                    for (let draft of cargoDrafts ()) draft.methods.checkpoint().revert();
                    for (let draft of cruiseDrafts()) draft.methods.checkpoint().revert();
                });
            },
            Validation: activitiesValidation
        },
        isSavingPierPosition = S.value(false),
        pierPositionClient = ajax.Url<BerthReservationOptions, unknown>(() => Reservation.Url() ?? '', { useCache: true }),
        pierPositionValidation = ValidationWatcher(pierPositionClient),
        PierPositionSaveHandler : SaveHandler = {
            HasUnsavedChanges: () => pierPositionDraft.methods.isCaching(),
            IsSaving: () => isSavingPierPosition(),
            PerformSave: SavePierPosition,
            QuitChanges() {
                pierPositionDraft.methods.checkpoint().revert();
            },
            Validation: pierPositionValidation
        };

    // Clear validation errors when quitting changes.
    S(() => !ActivitiesSaveHandler  .HasUnsavedChanges() && activitiesValidation  ({}));
    S(() => !PierPositionSaveHandler.HasUnsavedChanges() && pierPositionValidation({}));

    return inPortOpsModel({
        Reservation: merged,
        CanEdit,
        AddNewCargoActivity : AddActivity(cargoOriginals , () => new CargoActivity ()),
        AddNewCruiseActivity: AddActivity(cruiseOriginals, () => new CruiseActivity()),
        RemoveCargoActivity : RemoveActivity(cargoOriginals , cargoDraftToMetadata ),
        RemoveCruiseActivity: RemoveActivity(cruiseOriginals, cruiseDraftToMetadata),
        CargoActivityHasUnsavedChanges : ActivityHasUnsavedChanges(cargoDraftToMetadata ),
        CruiseActivityHasUnsavedChanges: ActivityHasUnsavedChanges(cruiseDraftToMetadata),
        HasAnyPortOpsPersisted,
        ActivitiesSaveHandler,
        PierPositionSaveHandler
    });

    function AddActivity<T>(originals : DraftArray<T>, factory : () => T) {
        // Add a new original for the new activity. Our matching .map(...) call above will handle making a
        // matching draft object for it.
        return () => originals.signal.push(factory());
    }

    function RemoveActivity<T extends object>(
        originals : DraftArray<T>,
        draftToMetadata : WeakMap<T, DraftActivityMetadata<T>>
    ) {
        return (act : T) => {
            // CAREFUL: The activity removed will be one of the ones from the merged reservation's matching
            // ...Activities array. I.e. one of our DRAFT activities.
            const orig = draftToMetadata.get(act)?.original;
            if (orig) originals.signal.remove(orig);
        };
    }

    function ActivityHasUnsavedChanges<T extends { Id : () => number }>(
        draftToMetadata : WeakMap<T, DraftActivityMetadata<T>>
    ) {
        // CAREFUL: The activity queried about will be one of the ones from the merged reservation's matching
        // ...Activities array. I.e. one of our DRAFT activities.
        return (act : T) =>
            act.Id() === 0
         || (draftToMetadata.get(act)?.draftObject.methods.isCaching() ?? false);
    }

    async function SaveActivities() {
        if (isSavingActivities()) return;
        isSavingActivities(true);
        try {
            const
                // Snapshot request data as of when the user hit save, rather than waiting till after we
                // acquire the SaveMutex below. That's b/c by the time we acquire the SaveMutex, the user might
                // have gone on to make other activities edits that they may or may not be ready to save at
                // that point.
                reqData : BerthReservationOptions = {
                    Id: Reservation.Id(),
                    HasLayOpsOnly: hasLayOpsOnlyDraft.object.HasLayOpsOnly(),
                    CargoActivities : JSON.parse(JSON.stringify(cargoDrafts ().map(d => d.object))),
                    CruiseActivities: JSON.parse(JSON.stringify(cruiseDrafts().map(d => d.object))),
                },
                alreadyPersistedCargoIds = new Set(
                    Reservation.CargoActivities() .map(act => act.Id()).filter(id => id !== 0)
                ),
                alreadyPersistedCruiseIds = new Set(
                    Reservation.CruiseActivities().map(act => act.Id()).filter(id => id !== 0)
                ),
                unpersistedCargoOriginals  = cargoOriginals .signal().filter(act => act.Id() === 0),
                unpersistedCruiseOriginals = cruiseOriginals.signal().filter(act => act.Id() === 0),
                checkpoints = [
                    hasLayOpsOnlyDraft.methods.checkpoint(),
                    cargoOriginals .methods.checkpoint(),
                    cruiseOriginals.methods.checkpoint(),
                    ...cargoDrafts ().map(d => d.methods.checkpoint()),
                    ...cruiseDrafts().map(d => d.methods.checkpoint()),
                ];

            const guard = await SaveMutex.acquire();
            try {
                const
                    req = activitiesClient.Put(reqData),
                    resp = await req;

                S.freeze(() => {
                    if (resp.CargoActivities) {
                        // When updating our data cache w/ new cargo activity data, use unpersisted "original"
                        // cargo activities as templates. The idea is that we already have draft CargoActivity
                        // objects referencing those unpersisted originals, and we wanna try our best to hold
                        // onto those existing draft objects. If we didn't, we'd end up constructing NEW draft
                        // CargoActivity objects for all newly-persisted CargoActivities, thus throwing away any
                        // edits that the user might have made to those activities while our
                        // UpdateInPortActivities request was in flight.
                        //
                        // To do this matching of incoming response data to unpersisted originals, we'll...
                        // 1. Use a `null` template for any incoming CargoActivity data that matches on Id to
                        //    a persisted CargoActivity that we already knew of before we sent our
                        //    UpdateInPortActivities request. The data.CargoActivities.update(...) call will
                        //    select that existing activity to receive the updated data, so there's nothing
                        //    more we need to do in that case.
                        // 2. The remaining CargoActivity data objects are the ones that need to get matched up
                        //    to our unpersisted originals. We'll assume that the server will assign Ids to the
                        //    new activities in the SAME ORDER that we listed them in our request. Thus, we'll
                        //    walk through the incoming response data in ascending Id order.
                        resp.CargoActivities.sort((a1, a2) => a1.Id - a2.Id);
                        let i = 0;
                        const templates = resp.CargoActivities.map(act =>
                            alreadyPersistedCargoIds.has(act.Id) ? null : unpersistedCargoOriginals[i++] ?? null
                        );
                        // Remove returned activities from alreadyPersistedCargoIds so we can be sure to delete
                        // any that weren't returned.
                        for (let act of resp.CargoActivities) alreadyPersistedCargoIds.delete(act.Id);
                        data.CargoActivities.update(
                            req.Date,
                            resp.CargoActivities,
                            Array.from(alreadyPersistedCargoIds),
                            templates
                        );
                    }
                    if (resp.CruiseActivities) {
                        // Same matching logic for cruise activities as for cargo above.
                        resp.CruiseActivities.sort((a1, a2) => a1.Id - a2.Id);
                        let i = 0;
                        const templates = resp.CruiseActivities.map(act =>
                            alreadyPersistedCruiseIds.has(act.Id) ? null : unpersistedCruiseOriginals[i++] ?? null
                        );
                        for (let act of resp.CruiseActivities) alreadyPersistedCruiseIds.delete(act.Id);
                        data.CruiseActivities.update(
                            req.Date,
                            resp.CruiseActivities,
                            Array.from(alreadyPersistedCruiseIds),
                            templates
                        );
                    }
                    BerthReservation.update(Reservation, resp);
                });
                // A little bit hacky: The whole point of using our existing unpersisted "originals" as
                // templates for the data.FooActivites.update(...) calls above is so that we can hold onto
                // our existing draft activity objects that already reference those template activities.
                // However, there's an S timing issue w/ that: When we call data.FooActivities.update(...), it
                // takes multiple ticks of S's clock before the matching Reservation.FooActivities array
                // reflects the new data. But calling fooOriginals.methods.checkpoint().revert() causes the
                // DraftArray to revert to the Reservation.FooActivities value on the next tick. As a result,
                // our DraftArrays will temporarily expose the OLD Reservation.FooActivities value before
                // updating to the new value. To our .map(...) calls that construct our draft activity objects,
                // it'll thus look like any new activities temporarily disappeared from our DraftArrays before
                // reappearing. And as a result we'll STILL end up constructing new draft activity objects and
                // throwing away any pending user edits.
                //
                // An easy fix is just to revert our checkpoints in their own later S.freeze(...) call. That
                // way, we don't revert our DraftArrays until AFTER Reservation.FooActivities reflects the
                // newly save data.
                //
                // Note that we COULD try to solve all this by teaching our .map(...) calls above to remember
                // and reuse previous draft objects instead of using S timing tricks here. Except that falls
                // over in the following scenario: 1) User makes pending edits to an existing activity. 2)
                // Without saving their changes, user changes their mind and decides to instead delete that
                // activity. 3) Again w/o saving their changes, user changes their mind again and decides to
                // cancel all changes. In that case we WANT our .map(...) call to construct a fresh draft
                // object for the relevant activity so that it reappears w/o any pending changes.
                S.freeze(() => {
                    for (let checkpoint of checkpoints) checkpoint.revert();
                });
            } finally {
                guard.release();
            }
        } finally {
            isSavingActivities(false);
        }
    }

    async function SavePierPosition() {
        if (isSavingPierPosition()) return;
        isSavingPierPosition(true);
        try {
            const
                // Snapshot request data as of when the user hit save, for the same reasons as SaveActivities
                // above.
                reqData : BerthReservationOptions = JSON.parse(JSON.stringify(pierPositionDraft.object)),
                checkpoint = pierPositionDraft.methods.checkpoint(),
                guard = await SaveMutex.acquire();
            try {
                await pierPositionClient.Put(reqData);
                checkpoint.revert();
            } finally {
                guard.release();
            }
        } finally {
            isSavingPierPosition(false);
        }
    }
}

export type InPortOps = ReturnType<typeof InPortOps>;
export function InPortOps(
    footmarkAssignmentsModel : (params : FootmarkAssignmentsParams) => FootmarkAssignments,
    {
        Reservation,
        CanEdit,
        AddNewCargoActivity,
        AddNewCruiseActivity,
        RemoveCargoActivity,
        RemoveCruiseActivity,
        CargoActivityHasUnsavedChanges,
        CruiseActivityHasUnsavedChanges,
        HasAnyPortOpsPersisted,
        ActivitiesSaveHandler,
        PierPositionSaveHandler
    } : InPortOpsParams
) {
    const
        schedAuth = () => Reservation.Itinerary()?.Location()?.Berth()?.SchedulingAuthority() ?? null,
        knownActivityTypes = lift(() => schedAuth()?.CargoActivityTypes() ?? [])
            .filter(t => t.Active())
            .sort(CargoActivityType.DisplayOrder),
        knownCargoCategories = lift(() => schedAuth()?.CargoCategories() ?? [])
            .filter(c => c.Active())
            .sort(CargoCategory.DisplayOrder),
        knownTradeRoutes = lift(() => schedAuth()?.TradeRoutes() ?? [])
            .filter(r => r.Active())
            .sort(TradeRoute.DisplayOrder),
        // Scheduling authorities can choose whether or not to use the Trade Route field. If the scheduling
        // authority has defined no trade routes in their lookup list and this reservation has no existing
        // cargo activities that reference a trade route, then we'll hide the column completely.
        ShouldShowTradeRoute = S(() => knownTradeRoutes().length > 0 ||
            Reservation.CargoActivities().some(act => act.TradeRouteId() !== null)
        ),
        // Unlike trade route, activity type and cargo category are required to add a cargo activity. If we're
        // missing lookup list values for either AND this reservation has no existing 
        HasRequiredCargoActivityLookups = () =>
            knownActivityTypes  ().length > 0
         && knownCargoCategories().length > 0,
        CargoActivities = Reservation.CargoActivities.map(act => CargoActivityViewModel(
            act,
            () => CargoActivityHasUnsavedChanges(act),
            knownActivityTypes,
            knownCargoCategories,
            knownTradeRoutes,
            () => RemoveCargoActivity(act)
        )).orderBy(act => act.Model.Id() === 0 ? Number.MAX_SAFE_INTEGER : act.Model.Id()),
        CruiseActivities = Reservation.CruiseActivities.map(act => CruiseActivityViewModel(
            act,
            () => CruiseActivityHasUnsavedChanges(act),
            () => RemoveCruiseActivity(act)
        )).orderBy(act => act.Model.Id() === 0 ? Number.MAX_SAFE_INTEGER : act.Model.Id()),
        // As long as we have enough reference data to add a cargo activity, then we'll say to be valid we
        // need at least one cargo or cruise activity OR a Lay Only designation. But skip if reference data is
        // missing, which includes the case where this reservation has no scheduling authority. Also, note that
        // we AUTOMATICALLY set HasLayOpsOnly to true in some cases (see below).
        AreActivitiesRequired = () => HasRequiredCargoActivityLookups(),
        AreActivitiesValidForSave = S(() =>
            (
                !AreActivitiesRequired()
             || Reservation.HasLayOpsOnly()
             || Reservation.CargoActivities ().length > 0
             || Reservation.CruiseActivities().length > 0
            )
         && CargoActivities ().every(act => act.IsValidForSave())
         && CruiseActivities().every(act => act.IsValidForSave())
        );

    // Update HasLayOpsOnly as cargo/cruise activities are updated.
    S<{ cargo: CargoActivity[], cruise: CruiseActivity[] } | null>(prev => {
        const cargo = Reservation.CargoActivities(), cruise = Reservation.CruiseActivities();
        // Only update if this isn't our initial run
        if (prev) {
            if (cargo.length > 0 || cruise.length > 0) {
                // If we have any cargo or cruise activities, then can't be Lay Only.
                Reservation.HasLayOpsOnly(false);
            } else if (HasAnyPortOpsPersisted() && (prev.cargo.length > 0 || prev.cruise.length > 0)) {
                // Otherwise, so long as we've persisted some port ops info for this reservation, then we'll say
                // that removing all cargo and cruise activities AUTOMATICALLY means that the reservation is Lay
                // Only. I.e. this UI won't let you get BACK into the state HasLayOpsOnly = false AND no cargo
                // or cruise activities are present once any other state has been persisted.
                Reservation.HasLayOpsOnly(true);
            }
        }
        return { cargo, cruise };
    }, null);

    const FootmarkAssignments = footmarkAssignmentsModel({ Reservation, CanEdit });

    // Keep reservation length up-to-date when changing side to. This is purely for client-side purposes (fast
    // feedback when making changes) -- the server applies its own version of the same logic.
    S<VesselSide | null>(prevSideTo => {
        const sideTo = Reservation.SideTo();
        // Propagate portcall changes to non-cancelled reservations only, but update for SideTo changes whether
        // or not the reservation is cancelled.
        if (prevSideTo !== null && sideTo !== prevSideTo || Reservation.CancelledDate() === null) {
            const portcall = Reservation.Itinerary()?.PortCall();
            if (portcall) {
                const newLength = BerthReservation.calculateLength(
                    portcall.Vessel()?.Length ?? null,
                    portcall.Vessel()?.Beam ?? null,
                    portcall.LengthOnlyLength,
                    Reservation.SideTo(),
                    Reservation.Length.UOM()
                );
                // Apply new length so long as we computed a usable value, but only if this is a follow up run
                // of this computation (due to data changing). Otherwise, leave existing length as is whether
                // it agrees w/ our computed length or not.
                if (newLength?.Num() && prevSideTo !== null) Reservation.Length.set(newLength as Measurement);
            }
        }
        return sideTo;
    }, null);

    // TODO: Client-side validation of footmarks. We already have server-side validation, but client-side here
    // would be polite.

    const
        IsValidForSave = () => AreActivitiesValidForSave(),
        IsSaving = () => (
            ActivitiesSaveHandler  ?.IsSaving()
         || PierPositionSaveHandler?.IsSaving()
        ) ?? false;

    return {
        Reservation,
        CanEdit,
        AddNewCargoActivity,
        AddNewCruiseActivity,
        HasAnyPortOpsPersisted,
        ActivitiesSaveHandler,
        PierPositionSaveHandler,
        ShouldShowTradeRoute,
        HasRequiredCargoActivityLookups,
        CargoActivities,
        CruiseActivities,
        AreActivitiesRequired,
        AreActivitiesValidForSave,
        FootmarkAssignments,
        IsValidForSave,
        IsSaving,
    };
}

export const CARGO_ERROR_MISSING_ACTIVITY_TYPE  : number = 0x01;
export const CARGO_ERROR_MISSING_CARGO_CATEGORY : number = 0x02;
export const CARGO_ERROR_MISSING_TRADE_ROUTE    : number = 0x04;
export const CARGO_ERROR_MISSING_QUANTITY       : number = 0x08;
export const CARGO_ERROR_QUANTITY_NONPOSITIVE   : number = 0x10;

export type CargoActivityViewModel = ReturnType<typeof CargoActivityViewModel>;
function CargoActivityViewModel(
    activity : CargoActivity,
    hasUnsavedChanges : () => boolean,
    knownActivityTypes : () => CargoActivityType[],
    knownCargoCategories : () => CargoCategory[],
    knownTradeRoutes : () => TradeRoute[],
    onDelete : () => void
) {
    const
        Errors = S(() => {
            let errors = 0;
            // ActivityTypeId and CargoCategoryId are required always, even if there are no known options
            // available. As in, before you can add a cargo activity, there NEEDS to be a scheduling authority
            // and they NEED to have defined some activity types and cargo categories. We have logic elsewhere
            // to do graceful things if that isn't the case.
            if (activity.ActivityTypeId() === null)
                errors |= CARGO_ERROR_MISSING_ACTIVITY_TYPE;
            if (activity.CargoCategoryId() === null)
                errors |= CARGO_ERROR_MISSING_CARGO_CATEGORY;
            // Trade route is only required if there are some options available. Scheduling authorities can
            // choose not to use trade routes at all.
            if (activity.TradeRouteId() === null && knownTradeRoutes().length > 0)
                errors |= CARGO_ERROR_MISSING_TRADE_ROUTE;
            if (activity.Quantity.Num() === null)
                errors |= CARGO_ERROR_MISSING_QUANTITY;
            if ((activity.Quantity.Num() ?? 1) <= 0)
                errors |= CARGO_ERROR_QUANTITY_NONPOSITIVE;
            return errors;
        }),
        errorTracker = ErrorTracker(Errors, hasUnsavedChanges),
        AvailableActivityTypes = S(() => includeExisting(activity.ActivityType(), knownActivityTypes())),
        AvailableCargoCategories = S(() => includeExisting(activity.CargoCategory(), knownCargoCategories())),
        AvailableTradeRoutes = S(() => includeExisting(activity.TradeRoute(), knownTradeRoutes()));

    S.on(activity.CargoCategory, () => {
        const category = activity.CargoCategory();
        // Check hasUnsavedChanges so we can distinguish the user selecting a new cargo category from the user
        // cancelling pending changes. In the latter case we wanna avoid clobbering the persisted UOM, which
        // may or may not agree w/ the selected category's current UOM.
        if (category && hasUnsavedChanges()) activity.Quantity.UOM(category.UOM());
    }, undefined, true);

    return {
        Model: activity,
        Delete: onDelete,
        Errors,
        ...errorTracker,
        AvailableActivityTypes,
        AvailableCargoCategories,
        AvailableTradeRoutes,
    };
}

function includeExisting<T>(existing : T | null, known : readonly T[]) {
    return existing === null || known.includes(existing) ? known : [existing, ...known];
}


export const CRUISE_ERROR_MISSING_PASSENGER_COUNT : number = 0x01;
export const CRUISE_ERROR_DISEMBARK_NEGATIVE      : number = 0x02;
export const CRUISE_ERROR_EMBARK_NEGATIVE         : number = 0x04;
export const CRUISE_ERROR_IN_TRANSIT_NEGATIVE     : number = 0x08;
export const CRUISE_ERROR_CREW_NEGATIVE           : number = 0x10;

export type CruiseActivityViewModel = ReturnType<typeof CruiseActivityViewModel>;
function CruiseActivityViewModel(
    activity : CruiseActivity,
    hasUnsavedChanges : () => boolean,
    onDelete : () => void
) {
    const
        Errors = S(() => {
            let errors = 0;
            if (
                (activity.Disembark() ?? 0) === 0
             && (activity.Embark()    ?? 0) === 0
             && (activity.InTransit() ?? 0) === 0
            ) {
                errors |= CRUISE_ERROR_MISSING_PASSENGER_COUNT;
            }
            if ((activity.Disembark() ?? 0) < 0)
                errors |= CRUISE_ERROR_DISEMBARK_NEGATIVE;
            if ((activity.Embark() ?? 0) < 0)
                errors |= CRUISE_ERROR_EMBARK_NEGATIVE;
            if ((activity.InTransit() ?? 0) < 0)
                errors |= CRUISE_ERROR_IN_TRANSIT_NEGATIVE;
            if ((activity.Crew() ?? 0) < 0)
                errors |= CRUISE_ERROR_CREW_NEGATIVE;
            return errors;
        }),
        errorTracker = ErrorTracker(Errors, hasUnsavedChanges);

    return {
        Model: activity,
        Delete: onDelete,
        Errors,
        ...errorTracker,
    };
}

// A common validation pattern for both cargo and cruise activities abstracted as a small standalone function.
function ErrorTracker(errors : () => number, hasUnsavedChanges : () => boolean) {
    const
        // If there ARE errors, should we take ownership of them, meaning we treat them as something the user
        // needs to resolve via the In Port Ops UI before we'll allow saving this activity? Or should we deny
        // ownership, meaning we think the error comes from some external source, e.g. it's legacy data from
        // our old Tariff Activities UI, in which case we'll let the validation errors slide?
        shouldOwnErrors = S.value(S.sample(hasUnsavedChanges)),
        // We lock editing of an invalid activity unless/until we take ownership of its errors.
        IsEditingLocked = () => errors() !== 0 && !shouldOwnErrors(),
        IsValidForSave = () => errors() === 0 || !shouldOwnErrors();

    S(hadUnsaved => {
        const hasUnsaved = hasUnsavedChanges();
        // If there are any unsaved changes to this activity, then we'll automatically take ownership of all
        // errors. The user has to fix ALL errors before we'll allow saving the activity.
        if (hasUnsaved) shouldOwnErrors(true);
        // Conversely, if the user quits pending changes for this activity, then we'll go back to tolerating
        // errors. But make sure we only do this when the hasUnsavedChanges signal flips from true to false,
        // so that we still support explicitly unlocking this activity even if there are no unsaved changes.
        else if (hadUnsaved) shouldOwnErrors(false);
        return hasUnsaved;
    }, S.sample(hasUnsavedChanges));

    return {
        IsEditingLocked,
        IsValidForSave,
        UnlockEditing
    };

    function UnlockEditing() {
        shouldOwnErrors(true);
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/inportops/model.ts